import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FileModel, ListSearch } from '@ca/core/models';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as FileSearchSortUtils from '../../../../utils/file-search-sort/file-search-sort-utils';

/**
 * Component for displaying a dialog to remove invalid and failed conversion files.
 * Allows users to review and remove files with issues.
 */
@Component({
    selector: 'ca-remove-invalid-files-dialog',
    templateUrl: './remove-invalid-files-dialog.component.html',
    styleUrls: ['./remove-invalid-files-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RemoveInvalidFilesDialogComponent {
    /**
     * The list of invalid files to display in the dialog.
     * Updates the combined files and paged files when set.
     */
    @Input({ required: true }) set invalidFiles(invalidFiles: FileModel[] | []) {
        this._invalidFiles = invalidFiles;
        this.updateCombinedFiles();
    }
    /**
     * The list of files that failed conversion.
     * Updates the combined files and paged files when set.
     */
    @Input() set failedConversionFiles(failedConversionFiles: FileModel[] | []) {
        this._failedConversionFiles = failedConversionFiles;
        this.updateCombinedFiles();
    }
    @Input() datasetId: string;
    @Input() isSegmentation?: boolean;

    /**
     * Gets the list of invalid files.
     * This returns the collection of files that have been marked as invalid.
     *
     * @returns {FileModel[]} An array of `FileModel` objects representing the invalid files.
     */
    get invalidFiles(): FileModel[] {
        return this._invalidFiles || [];
    }

    /**
     * Gets the list of files that failed conversion.
     * This returns the collection of files that have a conversion status of 'Failed'.
     *
     * @returns {FileModel[]} An array of `FileModel` objects representing the files that failed conversion.
     */
    get failedConversionFiles(): FileModel[] {
        return this._failedConversionFiles || [];
    }

    pagedFiles: FileModel[] = [];
    pageSize = 10;
    currentSearchOptions: ListSearch = { pageNumber: 1, pageSize: this.pageSize, searchQuery: '', sortBy: '' };
    combinedFiles: FileModel[] = [];

    private _invalidFiles: FileModel[] = [];
    private _failedConversionFiles: FileModel[] = [];

    constructor(private activeModal: NgbActiveModal) {}

    /**
     * Dismisses the dialog without any action.
     */
    dismiss(): void {
        this.activeModal.dismiss();
    }

    /**
     * Closes the dialog and returns the list of invalid and failed conversion files.
     */
    remove(): void {
        this.activeModal.close(this.invalidFiles);
    }

    /**
     * Updates the current paged view of files based on provided search and sort parameters.
     * @param params - The search and sort options for displaying files.
     */
    onFileSearchOrSort(params: ListSearch): void {
        this.currentSearchOptions = params;
        this.pagedFiles = FileSearchSortUtils.getCurrentPageOfFiles(this.invalidFiles, this.currentSearchOptions);
    }

    /**
     * Updates the combined list of files and paginates them.
     * Combines invalid files and failed conversion files.
     */
    private updateCombinedFiles(): void {
        this.combinedFiles = [...this._invalidFiles, ...this._failedConversionFiles];
        this.pagedFiles = FileSearchSortUtils.getCurrentPageOfFiles(this.combinedFiles, this.currentSearchOptions);
    }
}
